import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Info, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import ChartRenderer from "./ChartRenderer";
import { exportChartPdf, exportChartPng } from "./chartExport";

// `exportMeta` is the turn's applied-filter block (ai:done.exports[0].exportMeta) — the
// export buttons put it on the downloaded artifact so the chart travels with its context.
// `done` gates the bar: capture only makes sense once the turn (and the card's entry
// animation) has settled. The capture ref wraps title/chart/caveat but NOT the button bar,
// so the exported image is the card content without the UI chrome.
export default function ChartCard({ spec, exportMeta, done }) {
  const captureRef = useRef(null);
  const [busy, setBusy] = useState(null); // null | "pdf" | "png"
  const [error, setError] = useState("");

  const onExport = async (kind, fn) => {
    if (busy || !captureRef.current) return;
    setBusy(kind);
    setError("");
    try {
      await fn({ node: captureRef.current, spec, exportMeta });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[sq-ai] chart ${kind} export failed:`, err);
      setError("Couldn't export the chart — please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="bg-white border border-gray-200 rounded-xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
    >
      <div ref={captureRef}>
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">{spec.title}</h3>
          {spec.subtitle && <p className="text-sm text-gray-500 mt-1">{spec.subtitle}</p>}
        </div>
        <ChartRenderer spec={spec} />
        {spec.caveat && (
          <div className="mt-5 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border-l-2 border-amber-400 rounded-r">
            <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-xs text-amber-900 italic leading-relaxed">{spec.caveat}</p>
          </div>
        )}
      </div>
      {done && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onExport("pdf", exportChartPdf)}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 transition-colors"
          >
            {busy === "pdf"
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /><span>Preparing…</span></>
              : <><FileText className="w-3.5 h-3.5" strokeWidth={2} /><span>PDF</span></>}
          </button>
          <button
            type="button"
            onClick={() => onExport("png", exportChartPng)}
            disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 transition-colors"
          >
            {busy === "png"
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /><span>Preparing…</span></>
              : <><ImageIcon className="w-3.5 h-3.5" strokeWidth={2} /><span>PNG</span></>}
          </button>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      )}
    </motion.div>
  );
}
